// WP3 dilim 2 — müşteri portalında "Kanal Mesajlarınız" kartı (salt okuma).
//
// client-os.ts yükleme sırasında `window` global'lerine yazdığı için node testinde içe
// aktarılamıyor; saf render burada durur ve tests/ui-client-os-mesajlar.test.js ile korunur.
//
// Yanıt alanı BİLİNÇLİ OLARAK YOK — müşterinin müşterisine giden her mesaj onay kapısından geçer.
// Konuşma kimliği JS dizesine gömülmez: data-konusma özniteliğinden okunur (tırnak kaçışlı).

import { escHtml, escAttr } from '../utils.js';

export interface PortalKonusma {
  id: string;
  channel: string;
  /** Konuşmanın markası (firma panelinde birden çok marka olabilir). */
  marka?: string;
  markaAdi?: string | null;
  kisiEtiketi: string | null;
  externalContactId: string | null;
  status: string;
  sonMesajZamani: string | null;
  mesajSayisi: number;
  eskiKayit: boolean;
  sonMesaj: null | { govde: string | null; yon: string | null; zaman: string | null };
}

export interface PortalKonusmaBaslik {
  id: string;
  channel: string;
  marka?: string;
  markaAdi?: string | null;
  kisiEtiketi: string | null;
  externalContactId: string | null;
  status: string;
}

export interface PortalMesaj {
  id: string;
  yon: string;
  yazan: 'musteri' | 'ekip' | 'sistem';
  govde: string | null;
  zaman: string;
  teslimDurumu: string;
}

export const KANAL_ETIKET: Record<string, string> = {
  telegram: 'Telegram', whatsapp: 'WhatsApp', instagram_dm: 'Instagram mesajı', instagram_comment: 'Instagram yorumu',
  facebook_page: 'Facebook mesajı', facebook_comment: 'Facebook yorumu', web_form: 'Web formu', email: 'E-posta', manual: 'Elle kayıt',
};

const DURUM_ETIKET: Record<string, string> = {
  open: 'Açık', waiting_customer: 'Müşteri yanıtı bekleniyor', waiting_agent: 'Ekip yanıtı bekleniyor', closed: 'Kapandı', archived: 'Arşiv',
};

const YAZAN_ETIKET: Record<PortalMesaj['yazan'], string> = { musteri: 'Müşteriniz', ekip: 'Ekibimiz', sistem: 'Sistem' };

function attr(s: unknown): string { return escAttr(escHtml(s ?? '')); }
function zaman(z: string | null | undefined): string { return z ? String(z).slice(0, 16).replace('T', ' ') : '—'; }
function kanal(c: string): string { return KANAL_ETIKET[c] || c; }
function kisi(k: { kisiEtiketi: string | null; externalContactId: string | null }): string {
  return k.kisiEtiketi || k.externalContactId || 'Bilinmeyen kişi';
}
function markaEtiketi(k: { marka?: string; markaAdi?: string | null }): string {
  return k.markaAdi || k.marka || '';
}

/** Firmanın konuşmaları birden çok markaya mı ait? Tek markalı firmada marka etiketi gösterilmez. */
export function cokMarkali(list: PortalKonusma[] | null | undefined): boolean {
  if (!Array.isArray(list)) return false;
  return new Set(list.map((k) => k.marka).filter(Boolean)).size > 1;
}

/** Liste kartı. `null` = uç kapalı ya da okunamadı → kart hiç çizilmez. */
export function mesajlarKartiHtml(list: PortalKonusma[] | null | undefined, hata = ''): string {
  if (!Array.isArray(list)) return '';
  const markaSutunu = cokMarkali(list);
  const satirlar = list.slice(0, 50).map((k) => {
    const onizleme = k.sonMesaj
      ? `${k.sonMesaj.yon === 'outbound' ? 'Ekibimiz: ' : ''}${k.sonMesaj.govde ?? ''}`
      : k.eskiKayit ? 'Geçmiş mesajlar henüz aktarılmadı' : 'Mesaj yok';
    return `<tr style="cursor:pointer" data-konusma="${attr(k.id)}" onclick="window.__coOpenMsg&&window.__coOpenMsg(this.dataset.konusma)">
      <td>${escHtml(kisi(k))}<div class="gb-card-sub" style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:340px">${escHtml(onizleme)}</div></td>
      ${markaSutunu ? `<td>${escHtml(markaEtiketi(k))}</td>` : ''}
      <td>${escHtml(kanal(k.channel))}</td>
      <td>${escHtml(zaman(k.sonMesajZamani))}</td>
    </tr>`;
  }).join('');
  const govde = list.length
    ? `<table class="gb-table"><thead><tr><th>Kişi</th>${markaSutunu ? '<th>Marka</th>' : ''}<th>Kanal</th><th>Son mesaj</th></tr></thead><tbody>${satirlar}</tbody></table>
       <div class="gb-card-sub" style="margin-top:6px">Mesajları görmek için bir konuşmaya tıklayın.</div>`
    : '<div class="gb-empty-sm">Henüz kanallarınızdan gelen bir mesaj yok.</div>';
  const hataSatiri = hata ? `<div class="gb-card-sub" style="margin-top:6px">${escHtml(hata)}</div>` : '';
  return `<div class="gb-card"><div class="gb-card-title">📨 Kanal Mesajlarınız <span class="gb-card-sub">${list.length} konuşma</span></div>
    <div class="gb-card-sub" style="margin:-4px 0 10px">Bağlı kanallarınızdan (ör. Telegram) müşterilerinizin yazdıkları. Bu bölüm yalnız görüntüleme içindir; buradan yanıt gönderilmez.</div>
    ${govde}${hataSatiri}</div>`;
}

/** Tek konuşmanın mesaj akışı. `markaGoster`: firma çok markalıysa başlıkta marka da yazılır. */
export function mesajAkisiHtml(konusma: PortalKonusmaBaslik, mesajlar: PortalMesaj[], markaGoster = false): string {
  const markaYazi = markaGoster && markaEtiketi(konusma) ? ` · ${markaEtiketi(konusma)}` : '';
  const akis = mesajlar.length
    ? mesajlar.map((m) => {
      const musteri = m.yazan === 'musteri';
      return `<div style="padding:9px 11px;border-radius:8px;margin-bottom:8px;background:${musteri ? 'var(--card2)' : 'var(--co-acc-soft, rgba(47,67,166,.08))'}">
        <div style="display:flex;justify-content:space-between;font-size:12px;color:var(--muted);margin-bottom:3px"><span>${escHtml(YAZAN_ETIKET[m.yazan] || m.yazan)}</span><span>${escHtml(zaman(m.zaman))}</span></div>
        <div style="font-size:14px;white-space:pre-wrap;line-height:1.5">${escHtml(m.govde ?? '')}</div></div>`;
    }).join('')
    : '<div class="gb-empty-sm">Bu konuşmanın geçmiş mesajları henüz bu görünüme aktarılmadı.</div>';
  return `<div class="gb-card"><div style="display:flex;justify-content:space-between;align-items:baseline;gap:10px;margin-bottom:8px">
      <button class="gb-meeting-btn" onclick="window.__coBackMsg&&window.__coBackMsg()">← Konuşmalar</button>
      <span class="gb-pill gb-pill-${konusma.status === 'closed' ? 'muted' : 'sapphire'}">${escHtml(DURUM_ETIKET[konusma.status] || konusma.status)}</span></div>
    <div class="gb-card-title">${escHtml(kisi(konusma))} <span class="gb-card-sub">${escHtml(kanal(konusma.channel) + markaYazi)}</span></div>
    <div style="margin:10px 0">${akis}</div>
    <div class="gb-card-sub">Yalnız görüntüleme — yanıtlar ekibimiz tarafından onaylanarak gönderilir.</div></div>`;
}
